import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const LogoContext = createContext();

/** Public pages (TV calendar, portals) may have no localStorage — read logo from Firestore. */
const pickLogoFromDoc = (data) => {
  if (!data) return null;
  return data.logoBase64 || data.logoUrl || data?.branding?.logoBase64 || data?.branding?.logoUrl || null;
};

const pickDarkLogoFromDoc = (data) => {
  if (!data) return null;
  return data.darkLogoBase64 || data.darkLogoUrl || data?.branding?.darkLogoBase64 || data?.branding?.darkLogoUrl || null;
};

const fetchPublicLogo = async () => {
  const companySnap = await getDoc(doc(db, 'settings', 'company'));
  const fromCompany = companySnap.exists() ? pickLogoFromDoc(companySnap.data()) : null;
  if (fromCompany) return fromCompany;

  const globalSnap = await getDoc(doc(db, 'settings', 'global'));
  return globalSnap.exists() ? pickLogoFromDoc(globalSnap.data()) : null;
};

const fetchPublicDarkLogo = async () => {
  const companySnap = await getDoc(doc(db, 'settings', 'company'));
  const fromCompany = companySnap.exists() ? pickDarkLogoFromDoc(companySnap.data()) : null;
  if (fromCompany) return fromCompany;

  const globalSnap = await getDoc(doc(db, 'settings', 'global'));
  return globalSnap.exists() ? pickDarkLogoFromDoc(globalSnap.data()) : null;
};

const cachePublicLogo = (resolved, isDark = false) => {
  if (!resolved) return;
  const urlKey = isDark ? 'appDarkLogoUrl' : 'appLogoUrl';
  const base64Key = isDark ? 'appDarkLogoBase64' : 'appLogoBase64';
  if (resolved.startsWith('data:')) {
    localStorage.setItem(base64Key, resolved);
    localStorage.setItem(urlKey, resolved);
  } else {
    localStorage.setItem(urlKey, resolved);
  }
};

const convertUrlToBase64 = (url, base64Key) => {
  const storedBase64 = localStorage.getItem(base64Key);
  if (storedBase64 || !url) return;

  const img = new Image();
  if (url.includes('firebasestorage.googleapis.com')) {
    img.crossOrigin = 'anonymous';
  }
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      localStorage.setItem(base64Key, canvas.toDataURL('image/png', 0.95));
    } catch (canvasError) {
      console.warn('Canvas conversion failed:', canvasError);
    }
  };
  img.onerror = (error) => {
    console.warn('Image loading failed during LogoContext init:', error);
  };
  img.src = url;
};

export function LogoProvider({ children }) {
  const [logoUrl, setLogoUrl] = useState(() => localStorage.getItem('appLogoUrl'));
  const [darkLogoUrl, setDarkLogoUrl] = useState(() => localStorage.getItem('appDarkLogoUrl'));

  const getDisplayLogo = useCallback((isDarkMode) => {
    if (isDarkMode && darkLogoUrl) return darkLogoUrl;
    return logoUrl;
  }, [logoUrl, darkLogoUrl]);

  useEffect(() => {
    const fetchGlobalLogos = async () => {
      if (!auth.currentUser) {
        const cachedBase64 = localStorage.getItem('appLogoBase64');
        if (cachedBase64) {
          setLogoUrl(cachedBase64);
        } else {
          const cachedLogo = localStorage.getItem('appLogoUrl');
          if (cachedLogo) setLogoUrl(cachedLogo);
        }

        const cachedDarkBase64 = localStorage.getItem('appDarkLogoBase64');
        if (cachedDarkBase64) {
          setDarkLogoUrl(cachedDarkBase64);
        } else {
          const cachedDarkLogo = localStorage.getItem('appDarkLogoUrl');
          if (cachedDarkLogo) setDarkLogoUrl(cachedDarkLogo);
        }

        try {
          const [resolved, resolvedDark] = await Promise.all([
            fetchPublicLogo(),
            fetchPublicDarkLogo(),
          ]);
          if (resolved) {
            setLogoUrl(resolved);
            cachePublicLogo(resolved, false);
          }
          if (resolvedDark) {
            setDarkLogoUrl(resolvedDark);
            cachePublicLogo(resolvedDark, true);
          }
        } catch (error) {
          if (error.code !== 'permission-denied') {
            console.error('Error fetching public logos:', error);
          }
        }
        return;
      }

      try {
        const settingsRef = doc(db, 'settings', 'global');
        const settingsDoc = await getDoc(settingsRef);

        if (settingsDoc.exists()) {
          const settings = settingsDoc.data();
          const globalLogoUrl = settings?.branding?.logoUrl;
          const globalLogoBase64 = settings?.branding?.logoBase64;
          const globalDarkLogoUrl = settings?.branding?.darkLogoUrl;
          const globalDarkLogoBase64 = settings?.branding?.darkLogoBase64;

          if (globalLogoUrl) {
            updateLogo(globalLogoUrl);
            if (globalLogoBase64) {
              localStorage.setItem('appLogoBase64', globalLogoBase64);
            } else {
              convertUrlToBase64(globalLogoUrl, 'appLogoBase64');
            }
          }

          if (globalDarkLogoUrl) {
            updateDarkLogo(globalDarkLogoUrl);
            if (globalDarkLogoBase64) {
              localStorage.setItem('appDarkLogoBase64', globalDarkLogoBase64);
            } else {
              convertUrlToBase64(globalDarkLogoUrl, 'appDarkLogoBase64');
            }
          }
        }
      } catch (error) {
        if (error.code !== 'permission-denied') {
          console.error('Error fetching global logos:', error);
        }
        const cachedLogo = localStorage.getItem('appLogoUrl');
        if (cachedLogo) setLogoUrl(cachedLogo);
        const cachedDarkLogo = localStorage.getItem('appDarkLogoUrl');
        if (cachedDarkLogo) setDarkLogoUrl(cachedDarkLogo);
      }
    };

    fetchGlobalLogos();

    const unsubscribe = auth.onAuthStateChanged(() => {
      fetchGlobalLogos();
    });

    return () => unsubscribe();
  }, []);

  const updateLogo = (newLogoUrl) => {
    if (newLogoUrl) {
      localStorage.setItem('appLogoUrl', newLogoUrl);
    } else {
      localStorage.removeItem('appLogoUrl');
    }
    setLogoUrl(newLogoUrl);

    window.dispatchEvent(new CustomEvent('appLogoChanged', {
      detail: { logoUrl: newLogoUrl },
    }));
  };

  const updateDarkLogo = (newDarkLogoUrl) => {
    if (newDarkLogoUrl) {
      localStorage.setItem('appDarkLogoUrl', newDarkLogoUrl);
    } else {
      localStorage.removeItem('appDarkLogoUrl');
    }
    setDarkLogoUrl(newDarkLogoUrl);

    window.dispatchEvent(new CustomEvent('appDarkLogoChanged', {
      detail: { darkLogoUrl: newDarkLogoUrl },
    }));
  };

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'appLogoUrl') setLogoUrl(e.newValue);
      if (e.key === 'appDarkLogoUrl') setDarkLogoUrl(e.newValue);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <LogoContext.Provider value={{ logoUrl, darkLogoUrl, updateLogo, updateDarkLogo, getDisplayLogo }}>
      {children}
    </LogoContext.Provider>
  );
}

export const useLogo = () => {
  const context = useContext(LogoContext);
  if (!context) {
    throw new Error('useLogo must be used within a LogoProvider');
  }
  return context;
};
